function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function rnd(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomUUID() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const FIRST_NAMES = [
  'Alice', 'Bob', 'Carol', 'David', 'Emma', 'Frank', 'Grace', 'Henry',
  'Iris', 'Jack', 'Karen', 'Leo', 'Mia', 'Noah', 'Olivia', 'Paul',
  'Quinn', 'Rachel', 'Sam', 'Tara', 'Uma', 'Victor', 'Wendy', 'Xavier',
  'Yara', 'Zoe', 'Aaron', 'Beth', 'Chris', 'Diana', 'Ethan', 'Fiona',
  'George', 'Hannah', 'Ivan', 'Julia', 'Kevin', 'Luna', 'Mike', 'Nora',
  'Oscar', 'Petra', 'Ryan', 'Sofia', 'Tyler', 'Ursula', 'Wade', 'Xena',
  'Yasmin', 'Zach',
];

const LAST_NAMES = [
  'Smith', 'Jones', 'Taylor', 'Brown', 'Wilson', 'Davis', 'Miller', 'Moore',
  'Anderson', 'Jackson', 'Martin', 'Thompson', 'White', 'Harris', 'Clark',
  'Lewis', 'Robinson', 'Walker', 'Hall', 'Allen', 'Young', 'King', 'Wright',
  'Scott', 'Green', 'Baker', 'Adams', 'Nelson', 'Carter', 'Mitchell',
  'Perez', 'Roberts', 'Turner', 'Phillips', 'Campbell', 'Parker', 'Evans',
  'Edwards', 'Collins', 'Stewart', 'Morris', 'Rogers', 'Reed', 'Cook',
  'Morgan', 'Bell', 'Murphy', 'Bailey', 'Rivera', 'Cooper',
];

const CITIES = [
  'London', 'New York', 'Tokyo', 'Paris', 'Berlin', 'Sydney', 'Toronto',
  'Dubai', 'Singapore', 'Seoul', 'Barcelona', 'Amsterdam', 'Vienna', 'Oslo',
  'Stockholm', 'Prague', 'Warsaw', 'Budapest', 'Lisbon', 'Athens', 'Istanbul',
  'Mumbai', 'Shanghai', 'São Paulo', 'Mexico City', 'Cairo', 'Nairobi',
  'Lagos', 'Johannesburg', 'Buenos Aires',
];

const WORDS = [
  'apple', 'breeze', 'castle', 'dragon', 'eclipse', 'forest', 'glacier',
  'harbor', 'island', 'jungle', 'kettle', 'lantern', 'meadow', 'nebula',
  'ocean', 'phoenix', 'quest', 'river', 'shadow', 'thunder', 'umbrella',
  'valley', 'whisper', 'xenon', 'yellow', 'zenith',
];

export const DYNAMIC_VARIABLES = {
  $randomInt:         () => String(rnd(0, 999)),
  $randomInteger:     () => String(rnd(0, 999)),
  $randomFloat:       () => String((Math.random() * 1000).toFixed(2)),
  $randomBoolean:     () => String(Math.random() < 0.5),
  $randomUUID:        randomUUID,
  $guid:              randomUUID,
  $timestamp:         () => String(Math.floor(Date.now() / 1000)),
  $isoTimestamp:      () => new Date().toISOString(),
  $randomFirstName:   () => pick(FIRST_NAMES),
  $randomLastName:    () => pick(LAST_NAMES),
  $randomFullName:    () => `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`,
  $randomEmail:       () => `${pick(FIRST_NAMES).toLowerCase()}${rnd(1, 999)}@example.com`,
  $randomUsername:    () => `user_${rnd(1000, 99999)}`,
  $randomPhoneNumber: () => `+1-${rnd(200, 999)}-${rnd(100, 999)}-${rnd(1000, 9999)}`,
  $randomCity:        () => pick(CITIES),
  $randomWord:        () => pick(WORDS),
};

export const DYNAMIC_VARIABLE_DOCS = {
  $randomInt:         'Random integer · 0–999',
  $randomInteger:     'Random integer · 0–999',
  $randomFloat:       'Random float · 0.00–999.99',
  $randomBoolean:     'true or false',
  $randomUUID:        'UUID v4',
  $guid:              'UUID v4 (alias for $randomUUID)',
  $timestamp:         'Unix timestamp (seconds)',
  $isoTimestamp:      'ISO 8601 datetime',
  $randomFirstName:   'Random first name',
  $randomLastName:    'Random last name',
  $randomFullName:    'Random full name',
  $randomEmail:       'Random email address',
  $randomUsername:    'Random username',
  $randomPhoneNumber: 'Random phone number',
  $randomCity:        'Random city name',
  $randomWord:        'Random word',
};

/** Returns the computed value for a dynamic variable, or null if the name is not a dynamic variable. */
export function resolveDynamicVariable(name) {
  const fn = DYNAMIC_VARIABLES[name.trim()];
  return fn ? fn() : null;
}

/** Returns true if the variable name is a known dynamic variable. */
export function isDynamicVariable(name) {
  return Object.prototype.hasOwnProperty.call(DYNAMIC_VARIABLES, name.trim());
}

/**
 * Filter dynamic variable names for autocomplete.
 * Prefix match wins; otherwise substring match on the name ($fir → $randomFirstName).
 */
export function filterDynamicVariableNames(prefix) {
  const raw = String(prefix ?? '$').trim().toLowerCase();
  if (!raw || raw === '$') {
    return Object.keys(DYNAMIC_VARIABLES);
  }
  const needle = raw.startsWith('$') ? raw.slice(1) : raw;
  const score = (name) => {
    const lower = name.toLowerCase();
    if (lower.startsWith(raw)) return 0;
    if (needle.length > 0 && lower.includes(needle)) return 1;
    return -1;
  };
  return Object.keys(DYNAMIC_VARIABLES)
    .filter((name) => score(name) >= 0)
    .sort((a, b) => {
      const diff = score(a) - score(b);
      if (diff !== 0) return diff;
      return a.localeCompare(b);
    });
}
